// ConsumerAffairs Mission Control — daily operating dashboard.
//
// Top line: CPA, RPL, VMD by category / channel / campaign / ad set / creative.
// Volatility: CPM, CTR, CVR rolling z-score alerts.
// Drift: form_score (modeled) vs VMD (actual realized revenue) gap over time.
//
// Data: GET /api/ca/mission-control (single unified payload).

import { useEffect, useMemo, useState } from 'react'
import {
  Chart as ChartJS, CategoryScale, LinearScale, LineElement, PointElement,
  BarElement, BarController, LineController, Filler, Tooltip, Legend,
} from 'chart.js'
import { Line } from 'react-chartjs-2'
import { authFetch } from '../lib/api'
import './CAStyles.css'
import './IndexCA.css'

ChartJS.register(CategoryScale, LinearScale, LineElement, PointElement, BarElement,
                 BarController, LineController, Filler, Tooltip, Legend)

interface Topline {
  spend?: number; matches?: number; actual_rev?: number; predicted_rev?: number
  cpa?: number; rpl?: number; vmd?: number; drift_pct?: number
  cpm?: number; ctr?: number; cvr?: number
  spend_delta_pct?: number; matches_delta_pct?: number
  cpa_delta?: number; rpl_delta?: number
}
interface CategoryRow {
  category: string; spend: number; matches: number
  actual_rev: number; predicted_rev?: number
  cpa: number; rpl: number; vmd: number; drift_pct: number; roas: number
}
interface ChannelRow {
  channel: string; spend: number; matches: number
  actual_rev: number; cpa: number; rpl: number; roas: number
}
interface CampaignRow {
  campaign_name: string; spend: number; matches: number
  impressions: number; clicks: number
  cpa: number; ctr: number; cpm: number; cvr: number
}
interface CreativeRow {
  ad_name: string; ad_id: string; adset_name?: string; campaign_name?: string
  format?: string
  spend: number; matches: number; impressions: number; clicks: number
  cpa: number; ctr: number; cpm: number; cvr: number; hook_rate?: number
}
interface TrendPoint {
  date: string; spend: number; matches: number
  actual_rev: number; predicted_rev: number
  cpa?: number; rpl?: number; cpm?: number; ctr?: number; cvr?: number
}
interface DriftPoint { date: string; predicted_rev: number; actual_rev: number; drift_pct: number }
interface AnomalyRow {
  date: string; channel: string
  cpm: number; cpm_z?: number
  ctr: number; ctr_z?: number
  cvr: number; cvr_z?: number
}

const fmt$  = (n?: number) => '$' + Math.round(n || 0).toLocaleString()
const fmtK$ = (n?: number) => {
  const v = Math.abs(n || 0)
  if (v >= 1_000_000) return '$' + ((n || 0) / 1_000_000).toFixed(1) + 'M'
  if (v >= 1_000)     return '$' + ((n || 0) / 1_000).toFixed(0) + 'K'
  return fmt$(n)
}
const fmtN  = (n?: number) => (n || 0).toLocaleString()
const fmtP  = (n?: number) => n == null ? '—' : (n >= 0 ? '+' : '') + n.toFixed(1) + '%'
const fmt2  = (n?: number) => n == null ? '—' : n.toFixed(2)
const deltaClass = (n?: number, inverse = false) => {
  if (n == null) return 'flat'
  const good = inverse ? n < 0 : n > 0
  return good ? 'pos' : 'neg'
}

type Tab = 'category' | 'channel' | 'campaign' | 'creative' | 'anomalies' | 'drift'

interface MissionControlResp {
  status: string; error?: string
  topline: Topline
  by_category: CategoryRow[]
  by_channel: ChannelRow[]
  by_campaign: CampaignRow[]
  by_creative: CreativeRow[]
  trends: TrendPoint[]
  drift: DriftPoint[]
  anomalies: AnomalyRow[]
}

export function IndexCA() {
  const [data, setData] = useState<MissionControlResp | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [tab, setTab] = useState<Tab>('category')
  const [trendMetric, setTrendMetric] = useState<'cpa' | 'rpl' | 'cpm' | 'ctr' | 'cvr'>('cpa')
  const [days, setDays] = useState(30)

  useEffect(() => {
    let cancelled = false
    setLoading(true); setError(null)
    ;(async () => {
      try {
        const r = await authFetch(`/api/ca/mission-control?days=${days}&_t=${Date.now()}`)
        const j = await r.json() as MissionControlResp
        if (cancelled) return
        if (j.status !== 'success') throw new Error(j.error || 'Unknown error')
        setData(j)
      } catch (e) {
        if (!cancelled) setError((e as Error).message)
      } finally {
        if (!cancelled) setLoading(false)
      }
    })()
    return () => { cancelled = true }
  }, [days])

  // Concise date label: 'May 29' regardless of input format.
  // Flask's default jsonify serializes datetime.date as RFC 2822
  // ('Wed, 29 May 2026 00:00:00 GMT') — that's where the GMT clutter
  // came from. Handle both that and ISO '2026-05-29'.
  const MONTHS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec']
  const niceDate = (d: string | undefined | null): string => {
    if (!d) return ''
    const s = String(d)
    // ISO: 2026-05-29 or 2026-05-29T...
    const iso = s.match(/^(\d{4})-(\d{2})-(\d{2})/)
    if (iso) return `${MONTHS[parseInt(iso[2]) - 1]} ${parseInt(iso[3])}`
    // RFC 2822: 'Wed, 29 May 2026 00:00:00 GMT'
    const rfc = s.match(/(\d{1,2})\s+(Jan|Feb|Mar|Apr|May|Jun|Jul|Aug|Sep|Oct|Nov|Dec)\s+\d{4}/)
    if (rfc) return `${rfc[2]} ${parseInt(rfc[1])}`
    // Fallback: try Date parse
    const dt = new Date(s)
    if (!isNaN(dt.getTime())) return `${MONTHS[dt.getMonth()]} ${dt.getDate()}`
    return s
  }

  // Trend summary (current / min / max / avg) for the chart header.
  const trendSummary = useMemo(() => {
    if (!data?.trends?.length) return null
    const values = data.trends.map((p) => (p[trendMetric] as number) ?? 0).filter((v) => v > 0)
    if (!values.length) return null
    const last  = values[values.length - 1]
    const first = values[0]
    const min   = Math.min(...values)
    const max   = Math.max(...values)
    const avg   = values.reduce((s, v) => s + v, 0) / values.length
    const deltaPct = first > 0 ? ((last - first) / first) * 100 : 0
    return { last, first, min, max, avg, deltaPct }
  }, [data, trendMetric])

  const trendChart = useMemo(() => {
    if (!data?.trends?.length) return null
    const labels = data.trends.map((p) => niceDate(p.date))
    const values = data.trends.map((p) => (p[trendMetric] as number) ?? 0)
    return {
      labels,
      datasets: [{
        label: trendMetric.toUpperCase(),
        data: values,
        borderColor: 'rgb(59, 130, 246)',
        backgroundColor: 'rgba(59, 130, 246, 0.08)',
        borderWidth: 2,
        pointRadius: 0,
        pointHoverRadius: 4,
        tension: 0.35,
        fill: true,
      }],
    }
  }, [data, trendMetric])

  const driftChart = useMemo(() => {
    if (!data?.drift?.length) return null
    const labels = data.drift.map((p) => niceDate(p.date))
    return {
      labels,
      datasets: [
        {
          label: 'Form-score Predicted',
          data: data.drift.map((p) => p.predicted_rev),
          borderColor: 'rgba(180,180,180,0.7)',
          borderWidth: 1.5,
          pointRadius: 0,
          borderDash: [4, 3],
          fill: false,
        },
        {
          label: 'Actual (VMD)',
          data: data.drift.map((p) => p.actual_rev),
          borderColor: 'rgb(59, 130, 246)',
          backgroundColor: 'rgba(59, 130, 246, 0.1)',
          borderWidth: 2.5,
          pointRadius: 0,
          tension: 0.3,
          fill: true,
        },
      ],
    }
  }, [data])

  if (loading) return <div className="cax-load">Loading mission control…</div>
  if (error)   return <div className="cax-load err">Error: {error}</div>
  if (!data)   return <div className="cax-load">No data</div>

  const t = data.topline

  return (
    <div className="cax-wrap">
      {/* Header */}
      <div className="cax-head">
        <div>
          <div className="cax-eyebrow">Mission Control · ConsumerAffairs</div>
          <h1 className="cax-h1">Daily Operating View</h1>
        </div>
        <select className="cax-days" value={days} onChange={(e) => setDays(parseInt(e.target.value))}>
          <option value={7}>Last 7 days</option>
          <option value={14}>Last 14 days</option>
          <option value={30}>Last 30 days</option>
          <option value={60}>Last 60 days</option>
        </select>
      </div>

      {/* KPI cards */}
      <div className="cax-kpi-row">
        <div className="cax-kpi">
          <div className="cax-kpi-label">Spend</div>
          <div className="cax-kpi-value">{fmtK$(t.spend)}</div>
          <div className={`cax-kpi-delta ${deltaClass(t.spend_delta_pct)}`}>{fmtP(t.spend_delta_pct)} vs prev</div>
        </div>
        <div className="cax-kpi">
          <div className="cax-kpi-label">Match Requests</div>
          <div className="cax-kpi-value">{fmtN(t.matches)}</div>
          <div className={`cax-kpi-delta ${deltaClass(t.matches_delta_pct)}`}>{fmtP(t.matches_delta_pct)} vs prev</div>
        </div>
        <div className="cax-kpi">
          <div className="cax-kpi-label">CPA <span className="muted">(spend÷matches)</span></div>
          <div className="cax-kpi-value">{fmt$(t.cpa)}</div>
          <div className={`cax-kpi-delta ${deltaClass(t.cpa_delta, true)}`}>
            {t.cpa_delta == null ? '—' : (t.cpa_delta >= 0 ? '+' : '') + t.cpa_delta.toFixed(2)} vs prev
          </div>
        </div>
        <div className="cax-kpi">
          <div className="cax-kpi-label">RPL <span className="muted">(rev÷matches)</span></div>
          <div className="cax-kpi-value">{fmt$(t.rpl)}</div>
          <div className={`cax-kpi-delta ${deltaClass(t.rpl_delta)}`}>
            {t.rpl_delta == null ? '—' : (t.rpl_delta >= 0 ? '+' : '') + t.rpl_delta.toFixed(2)} vs prev
          </div>
        </div>
        <div className="cax-kpi">
          <div className="cax-kpi-label">VMD <span className="muted">(OpRev/lead)</span></div>
          <div className="cax-kpi-value">{fmt$(t.vmd)}</div>
          <div className="cax-kpi-delta">{fmtP(t.drift_pct)} form-score drift</div>
        </div>
      </div>

      {/* Trend chart with metric toggle */}
      <section className="cax-card">
        <div className="cax-card-head">
          <h2 className="cax-h2">{trendMetric.toUpperCase()}: daily trend</h2>
          <div className="cax-metric-tabs">
            {(['cpa', 'rpl', 'cpm', 'ctr', 'cvr'] as const).map((m) => (
              <button key={m}
                      className={`cax-metric-tab ${trendMetric === m ? 'active' : ''}`}
                      onClick={() => setTrendMetric(m)}>{m.toUpperCase()}</button>
            ))}
          </div>
        </div>
        {trendSummary && (
          <div className="cax-trend-stats">
            <div className="cax-trend-stat">
              <div className="cax-trend-stat-label">CURRENT</div>
              <div className="cax-trend-stat-value">
                {trendMetric === 'cpa' || trendMetric === 'rpl' || trendMetric === 'cpm'
                  ? '$' + trendSummary.last.toFixed(2)
                  : trendSummary.last.toFixed(2) + '%'}
              </div>
            </div>
            <div className="cax-trend-stat">
              <div className="cax-trend-stat-label">AVG · {days}d</div>
              <div className="cax-trend-stat-value muted">
                {trendMetric === 'cpa' || trendMetric === 'rpl' || trendMetric === 'cpm'
                  ? '$' + trendSummary.avg.toFixed(2)
                  : trendSummary.avg.toFixed(2) + '%'}
              </div>
            </div>
            <div className="cax-trend-stat">
              <div className="cax-trend-stat-label">RANGE</div>
              <div className="cax-trend-stat-value muted">
                {trendMetric === 'cpa' || trendMetric === 'rpl' || trendMetric === 'cpm'
                  ? `$${trendSummary.min.toFixed(0)}–$${trendSummary.max.toFixed(0)}`
                  : `${trendSummary.min.toFixed(2)}%–${trendSummary.max.toFixed(2)}%`}
              </div>
            </div>
            <div className="cax-trend-stat">
              <div className="cax-trend-stat-label">VS {days}d AGO</div>
              <div className={`cax-trend-stat-value ${trendSummary.deltaPct >= 0
                ? (trendMetric === 'cpa' || trendMetric === 'cpm' ? 'neg' : 'pos')
                : (trendMetric === 'cpa' || trendMetric === 'cpm' ? 'pos' : 'neg')}`}>
                {trendSummary.deltaPct >= 0 ? '+' : ''}{trendSummary.deltaPct.toFixed(1)}%
              </div>
            </div>
          </div>
        )}
        <div className="cax-chart">
          {trendChart && <Line data={trendChart} options={{
            responsive: true, maintainAspectRatio: false,
            plugins: {
              legend: { display: false },
              tooltip: {
                mode: 'index', intersect: false, displayColors: false,
                callbacks: {
                  // Force tooltip body to show metric + value with proper unit.
                  label: (ctx: any) => {
                    const v = ctx.parsed.y
                    const unit = (trendMetric === 'cpa' || trendMetric === 'rpl' || trendMetric === 'cpm')
                      ? '$' : '%'
                    return `${trendMetric.toUpperCase()}: ${unit === '$' ? '$' + v.toFixed(2) : v.toFixed(2) + '%'}`
                  },
                },
              },
            },
            interaction: { mode: 'nearest', intersect: false },
            scales: {
              x: {
                title: { display: true, text: 'Date', font: { size: 11, weight: 'bold' }, color: 'rgba(120,120,120,0.85)' },
                grid: { display: false },
                ticks: {
                  autoSkip: true, maxTicksLimit: 5,
                  maxRotation: 0, minRotation: 0,
                  font: { size: 11 }, color: 'rgba(120,120,120,0.85)',
                },
                border: { display: false },
              },
              y: {
                title: {
                  display: true,
                  text: `${trendMetric.toUpperCase()}${(trendMetric === 'cpa' || trendMetric === 'rpl' || trendMetric === 'cpm') ? ' ($)' : ' (%)'}`,
                  font: { size: 11, weight: 'bold' }, color: 'rgba(120,120,120,0.85)',
                },
                grid: { color: 'rgba(120,120,120,0.10)', drawTicks: false },
                ticks: { font: { size: 11 }, color: 'rgba(120,120,120,0.85)', padding: 8 },
                border: { display: false },
                beginAtZero: false,
              },
            },
          }} />}
        </div>
      </section>

      {/* Drilldown tabs */}
      <section className="cax-card">
        <div className="cax-card-head">
          <h2 className="cax-h2">Performance by</h2>
          <div className="cax-tabs">
            <button className={`cax-tab ${tab === 'category' ? 'active' : ''}`} onClick={() => setTab('category')}>Category</button>
            <button className={`cax-tab ${tab === 'channel' ? 'active' : ''}`} onClick={() => setTab('channel')}>Channel</button>
            <button className={`cax-tab ${tab === 'campaign' ? 'active' : ''}`} onClick={() => setTab('campaign')}>Campaign</button>
            <button className={`cax-tab ${tab === 'creative' ? 'active' : ''}`} onClick={() => setTab('creative')}>Creative</button>
            <button className={`cax-tab ${tab === 'anomalies' ? 'active' : ''}`} onClick={() => setTab('anomalies')}>
              Anomalies {data.anomalies.length > 0 && <span className="cax-tab-badge">{data.anomalies.length}</span>}
            </button>
            <button className={`cax-tab ${tab === 'drift' ? 'active' : ''}`} onClick={() => setTab('drift')}>Form-score Drift</button>
          </div>
        </div>

        {tab === 'category' && (
          <table className="cax-table">
            <thead><tr>
              <th>Category</th><th>Spend</th><th>Matches</th><th>CPA</th><th>RPL</th><th>VMD</th><th>Drift</th><th>ROAS</th>
            </tr></thead>
            <tbody>{data.by_category.map((r, i) => (
              <tr key={i}>
                <td>{r.category}</td>
                <td className="num">{fmtK$(r.spend)}</td>
                <td className="num">{fmtN(r.matches)}</td>
                <td className="num">{fmt$(r.cpa)}</td>
                <td className="num">{fmt$(r.rpl)}</td>
                <td className="num">{fmt$(r.vmd)}</td>
                <td className={`num ${Math.abs(r.drift_pct) > 20 ? 'warn' : ''}`}>{fmtP(r.drift_pct)}</td>
                <td className={`num ${r.roas >= 1 ? 'pos' : 'neg'}`}>{fmt2(r.roas)}×</td>
              </tr>
            ))}</tbody>
          </table>
        )}

        {tab === 'channel' && (
          <table className="cax-table">
            <thead><tr>
              <th>Channel</th><th>Spend</th><th>Matches</th><th>CPA</th><th>RPL</th><th>ROAS</th>
            </tr></thead>
            <tbody>{data.by_channel.map((r, i) => (
              <tr key={i}>
                <td>{r.channel}</td>
                <td className="num">{fmtK$(r.spend)}</td>
                <td className="num">{fmtN(r.matches)}</td>
                <td className="num">{fmt$(r.cpa)}</td>
                <td className="num">{fmt$(r.rpl)}</td>
                <td className={`num ${r.roas >= 1 ? 'pos' : 'neg'}`}>{fmt2(r.roas)}×</td>
              </tr>
            ))}</tbody>
          </table>
        )}

        {tab === 'campaign' && (
          <table className="cax-table">
            <thead><tr>
              <th>Campaign</th><th>Spend</th><th>Matches</th><th>CPA</th><th>CPM</th><th>CTR</th><th>CVR</th>
            </tr></thead>
            <tbody>{data.by_campaign.map((r, i) => (
              <tr key={i}>
                <td>{r.campaign_name}</td>
                <td className="num">{fmtK$(r.spend)}</td>
                <td className="num">{fmtN(r.matches)}</td>
                <td className="num">{fmt$(r.cpa)}</td>
                <td className="num">{fmt$(r.cpm)}</td>
                <td className="num">{fmt2(r.ctr)}%</td>
                <td className="num">{fmt2(r.cvr)}%</td>
              </tr>
            ))}</tbody>
          </table>
        )}

        {tab === 'creative' && (
          <table className="cax-table">
            <thead><tr>
              <th>Ad</th><th>Ad set</th><th>Format</th><th>Spend</th><th>Matches</th><th>CPA</th><th>CTR</th><th>Hook</th><th>CVR</th>
            </tr></thead>
            <tbody>{data.by_creative.map((r, i) => (
              <tr key={i}>
                <td className="truncate" title={r.ad_name}>{r.ad_name}</td>
                <td className="truncate muted" title={r.adset_name || ''}>{r.adset_name || '—'}</td>
                <td>{r.format || '—'}</td>
                <td className="num">{fmtK$(r.spend)}</td>
                <td className="num">{fmtN(r.matches)}</td>
                <td className="num">{fmt$(r.cpa)}</td>
                <td className="num">{fmt2(r.ctr)}%</td>
                <td className="num muted">{r.hook_rate != null ? `${fmt2(r.hook_rate)}%` : '—'}</td>
                <td className="num">{fmt2(r.cvr)}%</td>
              </tr>
            ))}</tbody>
          </table>
        )}

        {tab === 'anomalies' && (
          data.anomalies.length === 0 ? (
            <div className="cax-empty">No volatility anomalies in last 7 days (rolling z-score &lt; 2).</div>
          ) : (
            <table className="cax-table">
              <thead><tr>
                <th>Date</th><th>Channel</th>
                <th>CPM</th><th>CPM z-score</th>
                <th>CTR</th><th>CTR z-score</th>
                <th>CVR</th><th>CVR z-score</th>
              </tr></thead>
              <tbody>{data.anomalies.map((a, i) => (
                <tr key={i}>
                  <td>{niceDate(a.date)}</td>
                  <td>{a.channel}</td>
                  <td className="num">{fmt2(a.cpm)}</td>
                  <td className={`num ${Math.abs(a.cpm_z ?? 0) > 2 ? 'warn' : ''}`}>{fmt2(a.cpm_z)}</td>
                  <td className="num">{fmt2(a.ctr)}%</td>
                  <td className={`num ${Math.abs(a.ctr_z ?? 0) > 2 ? 'warn' : ''}`}>{fmt2(a.ctr_z)}</td>
                  <td className="num">{fmt2(a.cvr)}%</td>
                  <td className={`num ${Math.abs(a.cvr_z ?? 0) > 2 ? 'warn' : ''}`}>{fmt2(a.cvr_z)}</td>
                </tr>
              ))}</tbody>
            </table>
          )
        )}

        {tab === 'drift' && (
          <div>
            <p className="cax-note">
              <strong>Form-score</strong> is the modeled revenue prediction at form-submit time.
              <strong> VMD/Actual</strong> is the realized operating revenue per lead. Persistent gap
              means the scoring model is mis-calibrated and needs retraining.
            </p>
            <div className="cax-chart">
              {driftChart && <Line data={driftChart} options={{
                responsive: true, maintainAspectRatio: false,
                plugins: {
                  legend: { display: true, position: 'top', labels: { font: { size: 11 }, boxWidth: 12, padding: 14 } },
                  tooltip: {
                    mode: 'index', intersect: false,
                    callbacks: { label: (ctx: any) => `${ctx.dataset.label}: $${Math.round(ctx.parsed.y).toLocaleString()}` },
                  },
                },
                interaction: { mode: 'nearest', intersect: false },
                scales: {
                  x: {
                    title: { display: true, text: 'Date', font: { size: 11, weight: 'bold' }, color: 'rgba(120,120,120,0.85)' },
                    grid: { display: false },
                    ticks: { autoSkip: true, maxTicksLimit: 5, maxRotation: 0, minRotation: 0, font: { size: 11 }, color: 'rgba(120,120,120,0.85)' },
                    border: { display: false },
                  },
                  y: {
                    title: { display: true, text: 'Revenue ($)', font: { size: 11, weight: 'bold' }, color: 'rgba(120,120,120,0.85)' },
                    grid: { color: 'rgba(120,120,120,0.10)', drawTicks: false },
                    ticks: { font: { size: 11 }, color: 'rgba(120,120,120,0.85)', padding: 8 },
                    border: { display: false },
                  },
                },
              }} />}
            </div>
          </div>
        )}
      </section>
    </div>
  )
}
